<template>
  <div class="container-fluid px-4 py-4" style="background:#f8fafc;min-height:100vh;">

    <!-- Return-to-classic strip -->
    <AnnouncementStrip
      v-if="!betaOnly"
      message="You're on the new Analytics Dashboard — switch back any time."
      cta-label="Use Classic Dashboard"
      cta-url="/analytics/sales"
      dismiss-key="hide_classic_return"
      @onTryNew="handleReturnToClassic"
    />

    <!-- Header -->
    <div
      class="d-flex align-items-center justify-content-between flex-wrap pb-3 mb-4"
      style="gap:12px;border-bottom:1px solid #e9ecef;"
    >
      <h4 class="text-primary mb-0" style="font-size:21px;font-weight:600;letter-spacing:-0.3px;">Analytics</h4>
      <div class="d-flex align-items-center" style="gap:8px;">
        <a
          v-if="!betaOnly"
          href="/analytics/sales"
          class="btn btn-sm btn-light d-inline-flex align-items-center"
          style="gap:6px;border-radius:8px;font-size:12px;border:1px solid #e2e8f0;"
        >
          <i class="bi bi-arrow-left"></i>
          Classic view
        </a>
        <button
          @click="fetchDashboardData"
          class="btn btn-sm btn-light d-inline-flex align-items-center"
          style="gap:6px;border-radius:8px;font-size:12px;border:1px solid #e2e8f0;"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise" :class="{ 'spin': loading }"></i>
          Refresh
        </button>
        <button
          @click="showExportModal = true"
          class="btn btn-sm btn-dark d-inline-flex align-items-center"
          style="gap:6px;border-radius:8px;font-size:12px;"
        >
          <i class="bi bi-download"></i>
          Export
        </button>
      </div>
    </div>

    <!-- Filter pills -->
    <div class="d-flex flex-wrap align-items-center mb-4" style="gap:8px;">
      <label class="d-inline-flex align-items-center mb-0" style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#fff;">
        <span style="font-size:12px;color:#94a3b8;">Date</span>
        <select
          v-model="dateRange"
          @change="handleDateRangeChange"
          style="border:none;background:transparent;font-size:12px;font-weight:600;color:#0f172a;appearance:none;"
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="week">This week</option>
          <option value="month">This month</option>
          <option value="custom">Custom</option>
        </select>
      </label>

      <date-range-picker
        v-if="dateRange === 'custom'"
        v-model="customRange"
        :auto-apply="true"
        :ranges="false"
        opens="right"
        :append-to-body="true"
        class="custom-range-picker"
      >
        <template #input="picker">
          <span class="d-inline-flex align-items-center" style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#fff;font-size:12px;font-weight:600;color:#0f172a;cursor:pointer;">
            <i class="bi bi-calendar-event text-secondary"></i>
            <template v-if="picker.startDate && picker.endDate">
              {{ formatPickerDate(picker.startDate) }} – {{ formatPickerDate(picker.endDate) }}
            </template>
            <span v-else class="text-secondary" style="font-weight:500;">Select date range</span>
          </span>
        </template>
      </date-range-picker>

      <label v-if="branches.length > 1" class="d-inline-flex align-items-center mb-0" style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#fff;">
        <span style="font-size:12px;color:#94a3b8;">Branch</span>
        <select
          v-model="filters.branchId"
          @change="onBranchChange"
          style="border:none;background:transparent;font-size:12px;font-weight:600;color:#0f172a;appearance:none;"
        >
          <option :value="currentCompanyId">{{ currentCompanyName }} (current)</option>
          <option v-for="branch in branches" :key="branch.id" :value="branch.id">
            {{ branch.name }}
          </option>
        </select>
      </label>

      <label class="d-inline-flex align-items-center mb-0" style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#fff;">
        <span style="font-size:12px;color:#94a3b8;">Day</span>
        <select
          v-model="dayOfWeek"
          style="border:none;background:transparent;font-size:12px;font-weight:600;color:#0f172a;appearance:none;"
        >
          <option :value="null">Every day</option>
          <option v-for="d in ['monday','tuesday','wednesday','thursday','friday','saturday','sunday']" :key="d" :value="d" class="text-capitalize">{{ d.charAt(0).toUpperCase() + d.slice(1) }}s only</option>
        </select>
      </label>
      <span v-if="dayOfWeek" class="text-secondary" style="font-size:11px;">Day filter applies to the Summary and time-of-day chart</span>

      <!-- Live indicator -->
      <div class="d-flex align-items-center ml-auto" style="gap:6px;" v-if="dateRange === 'today'">
        <span
          class="d-inline-block rounded-circle bg-dark"
          style="width:6px;height:6px;animation:pulse 2s infinite;"
        ></span>
        <span style="font-size:11px;color:#94a3b8;">Live · updates every 5 min</span>
      </div>
    </div>

    <!-- Summary: the classic dashboard's numbers, links and modals — loads on its own, above the charts -->
    <!-- Breakdown: the classic dashboard's cards, links and modals -->
      <div class="d-flex align-items-center justify-content-between mb-3">
        <span style="font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.85);">Summary <i class="bi bi-question-circle text-secondary" style="font-size:12px;" title="Sales, payments, expenses and stock for the selected dates — the same figures and links as the classic dashboard."></i></span>
        <span class="text-secondary" style="font-size:11px;">{{ periodLabel }}{{ dayOfWeek ? ' · ' + dayOfWeek + 's only' : '' }}</span>
      </div>
      <ClassicBreakdown
        ref="breakdown"
        :start-date="filters.startDate"
        :end-date="filters.endDate"
        :day-of-week="dayOfWeek"
        :is-invoice-only="isInvoiceOnly"
        class="mb-4"
      />

    <!-- Error state -->
    <div
      v-if="error"
      class="bg-white border d-flex align-items-center mb-4"
      style="border-radius:14px;border-color:#e9ecef !important;padding:20px 24px;gap:16px;"
    >
      <i class="bi bi-exclamation-circle text-secondary" style="font-size:24px;flex-shrink:0;"></i>
      <div class="flex-grow-1">
        <p class="text-dark mb-1" style="font-size:13px;font-weight:500;">Failed to load dashboard</p>
        <p class="text-secondary mb-0" style="font-size:12px;">{{ error }}</p>
      </div>
      <button @click="fetchDashboardData" class="btn btn-sm btn-dark" style="border-radius:8px;font-size:12px;">
        Retry
      </button>
    </div>

    <!-- Loading skeleton -->
    <div v-else-if="loading">
      <div class="row" style="row-gap:20px;">
        <div class="col-12 col-xl-8">
          <div class="bg-white border" style="border-radius:14px;border-color:#e9ecef !important;height:520px;"></div>
        </div>
        <div class="col-12 col-xl-4">
          <div class="bg-white border" style="border-radius:14px;border-color:#e9ecef !important;height:520px;"></div>
        </div>
      </div>
    </div>

    <!-- Dashboard content -->
    <div v-else>

      <div class="row" style="row-gap:20px;">

        <!-- Performance panel: built from POS orders (salesMetrics) — not
             applicable to an invoices/quotes-only company, which has none. -->
        <div v-if="!isInvoiceOnly" class="col-12 col-xl-8">
          <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:24px;">

            <div class="mb-1" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Performance <i class="bi bi-question-circle text-secondary" style="font-size:12px;" title="Your headline metric and trend for the selected date range, with supporting KPIs below."></i></div>
            <div class="text-secondary mb-4" style="font-size:12px;">{{ periodLabel }}</div>

            <!-- Hero metrics: Total Income (excl. VAT) and Cash Received -->
            <div v-if="heroKpis.length" class="row mb-3" style="row-gap:16px;">
              <div v-for="hk in heroKpis" :key="hk.key" class="col-6" :class="heroKpis.length > 2 ? 'col-lg-3' : ''">
                <div class="text-secondary mb-1" style="font-size:13px;">
                  {{ hk.label }}
                  <i v-if="heroHelp[hk.key]" class="bi bi-question-circle" style="font-size:12px;" :title="heroHelp[hk.key]"></i>
                </div>
                <div class="d-flex align-items-center flex-wrap" style="gap:10px;">
                  <div :style="{ fontSize: heroKpis.length > 2 ? '22px' : '32px' }" style="font-weight:700;color:#0f172a;letter-spacing:-0.5px;line-height:1.1;">
                    {{ hk.formatted }}
                  </div>
                  <span
                    v-if="hk.key !== 'stock_value'"
                    class="d-inline-flex align-items-center bg-light"
                    :class="hk.comparison ? trendClass(hk) : 'text-secondary'"
                    style="font-size:12px;font-weight:600;padding:3px 9px;border-radius:20px;gap:4px;"
                  >
                    <i
                      v-if="hk.comparison"
                      :class="hk.comparison.direction === 'up' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"
                      style="font-size:9px;"
                    ></i>
                    {{ hk.comparison ? Math.abs(hk.comparison.change_percent) + '%' : 'N/A' }}
                  </span>
                </div>
                <div v-if="heroParts(hk).length" class="text-secondary mt-2" style="font-size:12px;">
                  <span v-for="(part, i) in heroParts(hk)" :key="part.label">{{ i ? ' · ' : '' }}{{ part.label }} {{ formatCurrency(part.amount) }}</span>
                </div>
              </div>
            </div>

            <!-- Chart -->
            <div style="margin:0 -4px 8px;">
              <IncomeBySourceChart v-if="showIncomeChart" :trend="incomeTrend" />
              <SalesChartPanel v-else :data="chartData" bare />
            </div>

            <!-- Secondary metrics -->
            <div style="border-top:1px solid #f1f5f9;padding-top:20px;margin-top:12px;">
              <KpiGrid :kpis="secondaryKpis" :start-date="filters.startDate" :end-date="filters.endDate" />
            </div>

          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-12" :class="isInvoiceOnly ? '' : 'col-xl-4'">

          <!-- Quick actions -->
          <div class="bg-white border mb-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
            <div class="mb-2" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">
              Quick actions <i class="bi bi-question-circle" style="font-size:11px;" title="Shortcuts to exports, alerts, and things needing your attention."></i>
            </div>

            <button
              @click="showExportModal = true"
              class="quick-action-row btn btn-link"
            >
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-download text-secondary"></i>
                Export data
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </button>

            <button @click="emailFullReport" class="quick-action-row btn btn-link">
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-envelope text-secondary"></i>
                Email full report
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </button>

            <a v-if="!betaOnly" href="/analytics/sales" class="quick-action-row">
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-arrow-left text-secondary"></i>
                Classic dashboard
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </a>

            <a
              v-if="!isInvoiceOnly && shiftClosingMetrics.pending_shift_closings > 0"
              href="/shift-cash-closings"
              class="quick-action-row"
            >
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-hourglass-split text-secondary"></i>
                Shift closings awaiting approval
              </span>
              <span class="d-inline-flex align-items-center justify-content-center bg-dark text-white" style="min-width:20px;height:20px;border-radius:10px;font-size:11px;font-weight:600;padding:0 6px;">
                {{ shiftClosingMetrics.pending_shift_closings }}
              </span>
            </a>

            <button
              v-if="alerts.length > 0"
              @click="scrollToAlerts"
              class="quick-action-row btn btn-link"
            >
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-exclamation-circle text-secondary"></i>
                Active alerts
              </span>
              <span class="d-inline-flex align-items-center justify-content-center bg-dark text-white" style="min-width:20px;height:20px;border-radius:10px;font-size:11px;font-weight:600;padding:0 6px;">
                {{ alerts.length }}
              </span>
            </button>
          </div>

          <!-- Receivables & Payables -->
          <div v-if="financeHighlights" class="bg-white border mb-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
            <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">
              Receivables &amp; Payables <i class="bi bi-question-circle" style="font-size:11px;" title="What customers owe you and what you owe suppliers, right now — not limited to the date range above."></i>
            </div>

            <div class="d-flex justify-content-between align-items-center mb-2">
              <span style="font-size:13px;color:#0f172a;">Customers owe you</span>
              <span style="font-size:15px;font-weight:700;color:#0f172a;">{{ formatCurrency(owed ? owed.customers_owe.total : financeHighlights.receivables.outstanding) }}</span>
            </div>
            <!-- Same total as the money-owed strip: every kind of unpaid balance. -->
            <template v-if="owed">
              <div v-for="part in owedParts" :key="part.label" class="d-flex justify-content-between align-items-center mb-1">
                <a :href="part.href" style="font-size:12px;color:#64748b;">{{ part.label }}</a>
                <span style="font-size:12px;color:#0f172a;">{{ formatCurrency(part.amount) }}</span>
              </div>
              <div class="mb-2"></div>
            </template>
            <div class="d-flex justify-content-between align-items-center mb-3" v-if="financeHighlights.receivables.overdue_90_plus > 0">
              <span style="font-size:12px;color:#fd4719;">90+ days overdue</span>
              <span style="font-size:12px;font-weight:600;color:#fd4719;">{{ formatCurrency(financeHighlights.receivables.overdue_90_plus) }}</span>
            </div>

            <div class="d-flex justify-content-between align-items-center mb-2">
              <span style="font-size:13px;color:#0f172a;">You owe suppliers</span>
              <span style="font-size:15px;font-weight:700;color:#0f172a;">{{ formatCurrency(financeHighlights.payables.outstanding) }}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-3" v-if="financeHighlights.payables.overdue_90_plus > 0">
              <span style="font-size:12px;color:#fd4719;">90+ days overdue</span>
              <span style="font-size:12px;font-weight:600;color:#fd4719;">{{ formatCurrency(financeHighlights.payables.overdue_90_plus) }}</span>
            </div>

            <div v-if="financeHighlights.invoices.invoiced > 0" class="d-flex justify-content-between align-items-center mb-2">
              <span style="font-size:12px;color:#64748b;">Invoices overdue · 30+ days</span>
              <span style="font-size:12px;color:#0f172a;">{{ formatCurrency(financeHighlights.receivables.overdue) }} · {{ formatCurrency(financeHighlights.receivables.overdue_30_plus) }}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <span style="font-size:12px;color:#64748b;">Payables overdue · 30+ days</span>
              <span style="font-size:12px;color:#0f172a;">{{ formatCurrency(financeHighlights.payables.overdue) }} · {{ formatCurrency(financeHighlights.payables.overdue_30_plus) }}</span>
            </div>
            <div v-if="financeHighlights.invoices.invoiced > 0" class="d-flex justify-content-between align-items-center mb-2">
              <span style="font-size:13px;color:#0f172a;">Invoiced (all time)</span>
              <span style="font-size:15px;font-weight:700;color:#0f172a;">{{ formatCurrency(financeHighlights.invoices.invoiced) }}</span>
            </div>
            <div v-if="financeHighlights.invoices.invoiced > 0" class="d-flex justify-content-between align-items-center mb-3">
              <span style="font-size:12px;color:#64748b;">Collected · Outstanding</span>
              <span style="font-size:12px;color:#0f172a;">{{ formatCurrency(financeHighlights.invoices.collected) }} · {{ formatCurrency(financeHighlights.invoices.outstanding) }}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-3" v-if="financeHighlights.invoices.overdue_count > 0">
              <span style="font-size:12px;color:#fd4719;">Overdue invoices</span>
              <span style="font-size:12px;font-weight:600;color:#fd4719;">{{ financeHighlights.invoices.overdue_count }}</span>
            </div>

            <a href="/invoices" class="quick-action-row">
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-receipt text-secondary"></i>
                Invoices
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </a>
            <a href="/reports/receivables" class="quick-action-row">
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-arrow-down-circle text-secondary"></i>
                Receivables aging
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </a>
            <a href="/reports/payables" class="quick-action-row">
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-arrow-up-circle text-secondary"></i>
                Payables aging
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </a>
            <a href="/reports" class="quick-action-row">
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-clipboard-data text-secondary"></i>
                All reports
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </a>
          </div>

          <!-- Accounting reports (Enterprise) -->
          <div v-if="isEnterprise" class="bg-white border mb-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
            <div class="mb-2" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">
              Accounting reports <i class="bi bi-question-circle" style="font-size:11px;" title="Download the primary financial statements and ledgers for the selected date range."></i>
            </div>

            <a href="/tax-ledgers" class="quick-action-row">
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-journal-text text-secondary"></i>
                Tax Ledgers
              </span>
              <i class="bi bi-chevron-right text-secondary" style="font-size:11px;"></i>
            </a>

            <button
              @click="downloadAccountReport('salestax')"
              :disabled="downloadingReport === 'salestax'"
              class="quick-action-row btn btn-link"
            >
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-receipt text-secondary"></i>
                Sales Tax Report
              </span>
              <i :class="downloadingReport === 'salestax' ? 'bi bi-arrow-clockwise spin' : 'bi bi-download'" class="text-secondary" style="font-size:11px;"></i>
            </button>

            <button
              @click="downloadAccountReport('pnl')"
              :disabled="downloadingReport === 'pnl'"
              class="quick-action-row btn btn-link"
            >
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-graph-up-arrow text-secondary"></i>
                Profit &amp; Loss
              </span>
              <i :class="downloadingReport === 'pnl' ? 'bi bi-arrow-clockwise spin' : 'bi bi-download'" class="text-secondary" style="font-size:11px;"></i>
            </button>

            <button
              @click="downloadAccountReport('generalledger')"
              :disabled="downloadingReport === 'generalledger'"
              class="quick-action-row btn btn-link"
            >
              <span class="d-inline-flex align-items-center" style="gap:10px;">
                <i class="bi bi-journal-richtext text-secondary"></i>
                General Ledger
              </span>
              <i :class="downloadingReport === 'generalledger' ? 'bi bi-arrow-clockwise spin' : 'bi bi-download'" class="text-secondary" style="font-size:11px;"></i>
            </button>
          </div>

          <!-- Payment breakdown: sourced from POS order payments (paymentMetrics) —
               not applicable to an invoices/quotes-only company. -->
          <div v-if="paymentBreakdown.length" class="bg-white border mb-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
            <PaymentBreakdownChart :data="paymentBreakdown" />
            <p class="text-secondary mb-0 mt-2" style="font-size:11px;line-height:1.5;">
              All money received in this period (sales, invoices, memberships, reservations, wallet top-ups) — may include payments for things charged earlier.
            </p>
            <a href="#" @click.prevent="$refs.breakdown && $refs.breakdown.openTransactions()" class="d-inline-flex align-items-center mt-2" style="font-size:12px;font-weight:600;gap:4px;">
              View all transactions
              <i class="bi bi-chevron-right" style="font-size:10px;"></i>
            </a>
          </div>

          <!-- Dukakit Pay -->
          <div
            v-if="hasDukakitPayAccount"
            class="bg-white border mb-4"
            style="border-radius:14px;border-color:#e9ecef !important;padding:20px;"
          >
            <div class="d-flex align-items-center mb-3" style="gap:8px;">
              <i class="bi bi-wallet2 text-secondary"></i>
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">{{ $platformBrand.name }} Pay</span>
              <i class="bi bi-question-circle text-secondary" style="font-size:11px;" :title="`Your ${$platformBrand.name} Pay wallet balance, available to withdraw to M-Pesa.`"></i>
            </div>
            <div class="text-secondary mb-1" style="font-size:12px;">Wallet balance</div>
            <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap:10px;">
              <div class="text-dark" style="font-size:20px;font-weight:700;letter-spacing:-0.3px;">
                {{ formatCurrency(dukakitAccountBalance) }}
              </div>
              <button
                v-if="dukakitAccountBalance > 100"
                @click="openWithdrawModal"
                class="btn btn-sm btn-dark"
                style="border-radius:8px;font-size:12px;"
              >
                Withdraw to M-Pesa
              </button>
            </div>
          </div>

          <!-- Buy Now Pay Later: a POS order sale term — not applicable to
               an invoices/quotes-only company. -->
          <div
            v-if="!isInvoiceOnly && (bnpl.outstanding_for_period.value > 0 || bnpl.outstanding_all_time.value > 0)"
            class="bg-white border"
            style="border-radius:14px;border-color:#e9ecef !important;padding:20px;"
          >
            <div class="d-flex align-items-center mb-3" style="gap:8px;">
              <i class="bi bi-clock-history text-secondary"></i>
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Buy Now Pay Later</span>
              <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Balance still owed on orders sold with Buy Now Pay Later."></i>
            </div>
            <div class="text-secondary mb-1" style="font-size:12px;">Owed from orders in this period</div>
            <div class="text-dark mb-3" style="font-size:20px;font-weight:700;letter-spacing:-0.3px;">
              {{ bnpl.outstanding_for_period.formatted }}
            </div>
            <div class="d-flex align-items-center justify-content-between pt-3" style="border-top:1px solid #f1f5f9;">
              <span class="text-secondary" style="font-size:12px;">All-time outstanding</span>
              <span class="text-dark" style="font-size:13px;font-weight:600;">{{ bnpl.outstanding_all_time.formatted }}</span>
            </div>
            <a :href="`/orders?bnpl=1&start-date=${filters.startDate}&end-date=${filters.endDate}`" class="d-inline-flex align-items-center mt-2" style="font-size:12px;font-weight:600;gap:4px;">
              View BNPL orders
              <i class="bi bi-chevron-right" style="font-size:10px;"></i>
            </a>
          </div>

        </div>
      </div>

      <!-- Money owed to / by the business right now -->
      <div v-if="owed" class="bg-white border mt-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
        <div class="row" style="row-gap:16px;">
          <div class="col-12 col-md-5">
            <div class="text-secondary mb-1" style="font-size:12px;">Customers owe you <i class="bi bi-question-circle" style="font-size:11px;" title="Unpaid invoices + Buy Now Pay Later order balances + unpaid booking balances + unpaid membership balances."></i></div>
            <div class="text-dark" style="font-size:20px;font-weight:700;">{{ formatCurrency(owed.customers_owe.total) }}</div>
            <div class="text-secondary" style="font-size:12px;">
              <a v-if="owed.customers_owe.invoices > 0" href="/reports/receivables" class="text-secondary">Invoices {{ formatCurrency(owed.customers_owe.invoices) }}</a>
              <a v-if="owed.customers_owe.bnpl > 0" :href="`/orders?bnpl=1`" class="text-secondary ml-2">BNPL {{ formatCurrency(owed.customers_owe.bnpl) }}</a>
              <a v-if="owed.customers_owe.bookings > 0" href="/reservations" class="text-secondary ml-2">Bookings {{ formatCurrency(owed.customers_owe.bookings) }}</a>
              <a v-if="owed.customers_owe.memberships > 0" href="/subscriptions" class="text-secondary ml-2">Memberships {{ formatCurrency(owed.customers_owe.memberships) }}</a>
            </div>
          </div>
          <div class="col-6 col-md-4">
            <div class="text-secondary mb-1" style="font-size:12px;">You owe suppliers</div>
            <a href="/reports/payables" class="text-dark d-block" style="font-size:20px;font-weight:700;">{{ formatCurrency(owed.you_owe.suppliers) }}</a>
          </div>
          <div v-if="hasDukakitPayAccount" class="col-6 col-md-3">
            <div class="text-secondary mb-1" style="font-size:12px;">Dukakit Pay balance</div>
            <div class="text-dark" style="font-size:20px;font-weight:700;">{{ formatCurrency(dukakitAccountBalance) }}</div>
          </div>
        </div>
      </div>

      <!-- Module blocks, biggest income source first; stock and manufacturing after. -->
      <div class="d-flex flex-column">

      <!-- Reservations: occupancy, rate, rooms, sources -->
      <ReservationBlock v-if="hasReservations" :style="{ order: blockOrder.Reservations }" :start-date="filters.startDate" :end-date="filters.endDate" />

      <!-- Memberships: member health, busiest times, expiring + lapsed with one-tap SMS -->
      <MembershipBlock
        v-if="hasSubscriptions"
        :style="{ order: blockOrder.Memberships }"
        :start-date="filters.startDate"
        :end-date="filters.endDate"
        :trend="incomeTrend"
      />

      <ManufacturingBlock v-if="hasManufacturing" style="order:90;" :start-date="filters.startDate" :end-date="filters.endDate" />

      <StockBlock v-if="stock && (stock.in_stock || stock.out_of_stock || stock.negative)" style="order:91;" :stock="stock" />

      <!-- Sales (POS): top products + sales by category, staff, time of day -->
      <div :style="{ order: blockOrder.Sales }">
      <!-- Top products + sales by category (POS and invoices), two columns. -->
      <div v-if="showPosSections || topProducts.length" class="row mt-4" style="row-gap:20px;">
        <div class="col-12 col-lg-6">
          <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Top products <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Best-selling products by revenue for the selected date range."></i></span>
              <span class="text-secondary" style="font-size:11px;">{{ periodLabel }}</span>
            </div>
            <TopProductsTable :products="topProducts" :start-date="filters.startDate" :end-date="filters.endDate" />
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Sales by category <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Revenue and product count grouped by category for the selected date range. Click a category to see its sales."></i></span>
              <span class="text-secondary" style="font-size:11px;">{{ periodLabel }}</span>
            </div>
            <CategoryPerformanceTable :categories="categoryPerformance" :start-date="filters.startDate" :end-date="filters.endDate" />
          </div>
        </div>
      </div>

      <!-- Staff performance: its own row -->
      <div v-if="showPosSections" class="row mt-4">
        <div class="col-12">
          <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Staff performance <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Orders, revenue, and discounts given per staff member for the selected date range."></i></span>
              <span class="text-secondary" style="font-size:11px;">{{ periodLabel }}</span>
            </div>
            <StaffPerformanceTable :staff="staffPerformance" :start-date="filters.startDate" :end-date="filters.endDate" />
          </div>
        </div>
      </div>

      <!-- Sales by time of day (classic "Sales Statistics") -->
      <div v-if="showPosSections" class="bg-white border mt-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Sales by time of day <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="When in the day your sales happen, for the selected dates."></i></span>
          <span class="text-secondary" style="font-size:11px;">{{ periodLabel }}{{ dayOfWeek ? ' · ' + dayOfWeek + 's only' : '' }}</span>
        </div>
        <dashboard-saleschart v-if="timeOfDayChart.labels.length" :chart-data="timeOfDayChart" :chart-options="{ responsive: true }" :height="90" />
        <p v-else class="text-secondary mb-0" style="font-size:12px;">No sales in this period</p>
      </div>
      </div>
      </div>

      <!-- Alerts -->
      <div v-if="alerts.length > 0" ref="alertsAnchor" class="mt-4">
        <AlertsPanel
          :alerts="alerts"
          @dismiss="dismissAlert"
        />
      </div>

    </div>

    <!-- Export Modal -->
    <ExportModal
      v-if="showExportModal"
      :filters="filters"
      @close="showExportModal = false"
    />

    <!-- Withdraw Modal -->
    <WithdrawModal
      v-if="showWithdrawModal"
      :balance="dukakitAccountBalance"
      @close="showWithdrawModal = false"
      @withdrawn="fetchDukakitBalance"
    />

  </div>
</template>

<script>
import { downloadFile } from '../../services/pdfDownload';
import axios from 'axios';
import KpiGrid from './KpiGrid.vue';
import SalesChartPanel from './SalesTrendChart.vue';
import IncomeBySourceChart from './IncomeBySourceChart.vue';
import MembershipBlock from './MembershipBlock.vue';
import ReservationBlock from './ReservationBlock.vue';
import StockBlock from './StockBlock.vue';
import ManufacturingBlock from './ManufacturingBlock.vue';
import PaymentBreakdownChart from './PaymentBreakdownChart.vue';
import TopProductsTable from './TopProductsTable.vue';
import CategoryPerformanceTable from './CategoryPerformanceTable.vue';
import StaffPerformanceTable from './StaffPerformanceTable.vue';
import AlertsPanel from './AlertsPanel.vue';
import ExportModal from './ExportModal.vue';
import WithdrawModal from './WithdrawModal.vue';
import AnnouncementStrip from '../Admin/AnnouncementStrip.vue';
import ClassicBreakdown from './ClassicBreakdown.vue';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import { mapGetters } from 'vuex';
import moment from 'moment';
import { recordVisit, setPreferredDashboard } from '../../services/dashboardPreference';
import branchSwitch from '../../services/branchSwitch';

const EMPTY_PERIOD = { labels: [], sales: [], net: [], orders: [], profit: [], aov: [], invoices: [] };
const HERO_KEY_PRIORITY = ['total_income', 'total_sales', 'net_sales', 'gross_sales'];
const HERO_SECOND_KEY = 'cash_received';
// Built from POS orders - hidden for a gym (subscriptions-only) business when empty.
const POS_ONLY_KEYS = ['total_sales', 'gross_sales', 'net_sales', 'total_orders', 'pending_orders', 'avg_order_value',
  'total_items_sold', 'avg_items_per_order', 'discount_total', 'refund_total', 'void_total', 'tax_collected', 'invoice_sales', 'outstanding_balance'];

export default {
  name: 'AnalyticsDashboard',

  components: {
    AnnouncementStrip,
    ClassicBreakdown,
    KpiGrid,
    SalesChartPanel,
    IncomeBySourceChart,
    MembershipBlock,
    ReservationBlock,
    StockBlock,
    ManufacturingBlock,
    PaymentBreakdownChart,
    TopProductsTable,
    CategoryPerformanceTable,
    StaffPerformanceTable,
    AlertsPanel,
    ExportModal,
    WithdrawModal,
    DateRangePicker
  },

  data() {
    return {
      loading: false,
      error: null,
      dateRange: 'today',
      dayOfWeek: null,
      timeOfDayChart: { labels: [], datasets: [] },
      incomeTrend: null,
      stock: null,
      owed: null,
      trendViewMode: 'hourly',
      customRange: { startDate: null, endDate: null },
      filters: {
        startDate: new Date().toISOString().split('T')[0],
        endDate:   new Date().toISOString().split('T')[0],
        branchId:  null,
        cashierId: null
      },
      branches: [],
      financeHighlights: null,
      kpis: [],
      chartData: { day: { ...EMPTY_PERIOD }, week: { ...EMPTY_PERIOD }, month: { ...EMPTY_PERIOD } },
      paymentBreakdown: [],
      bnpl: {
        outstanding_for_period: { value: 0, formatted: 'KES 0.00' },
        outstanding_all_time: { value: 0, formatted: 'KES 0.00' }
      },
      topProducts: [],
      categoryPerformance: [],
      staffPerformance: [],
      alerts: [],
      showExportModal: false,
      downloadingReport: null,
      dukakitAccountBalance: 0,
      hasDukakitPayAccount: true,
      showWithdrawModal: false,
      autoRefreshInterval: null,
      shiftClosingMetrics: {
        pending_shift_closings: 0,
        approved_closings_today: 0,
        today_cash_variance: 0,
        month_cash_variance: 0
      }
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    // Companies that joined from 1 Aug 2026 have no classic dashboard (Company::beta_dashboard_only).
    betaOnly() {
      return !!this.userDetails?.company?.beta_dashboard_only;
    },

    isEnterprise() {
      return (this.userDetails?.company?.subscription_plan ?? 0) > 2000;
    },

    // Same convention as BottomMenu.vue's isInvoiceOnly: a company whose
    // primary_use_case is invoices_quotes has no POS orders/shifts/products
    // to report on — the sections below built from Orders data (salesMetrics,
    // paymentMetrics, staff/category/top-products) would just show empty or
    // misleadingly-zero panels for it, so they're hidden rather than shown
    // broken.
    isInvoiceOnly() {
      return this.userDetails?.company?.companysettings?.primary_use_case === 'invoices_quotes';
    },

    currentCompanyId() {
      return this.userDetails?.company?.id ?? null;
    },

    currentCompanyName() {
      return this.userDetails?.company?.name ?? 'Current branch';
    },

    activePeriod() {
    // maps the dashboard's dateRange to the chart's period keys
    const map = { today: 'day', yesterday: 'day', week: 'week', month: 'month', custom: 'week' };
    return map[this.dateRange] || 'week';
  },
    periodLabel() {
      const map = {
        today:     'Today',
        yesterday: 'Yesterday',
        week:      'This week',
        month:     'This month',
        custom:    `${this.filters.startDate} – ${this.filters.endDate}`
      };
      return map[this.dateRange] || '';
    },


    heroKpi() {
      const byKey = key => this.kpis.find(k => k.key === key);
      return HERO_KEY_PRIORITY.map(byKey).find(Boolean) || this.kpis[0] || null;
    },

    // Earned · came in · kept · owned.
    heroKpis() {
      const byKey = key => this.kpis.find(k => k.key === key);
      const heroes = [this.heroKpi, byKey(HERO_SECOND_KEY)];
      if (this.heroKpi && this.heroKpi.key === 'total_income') heroes.push(byKey('net_profit'));
      if (this.stock && this.stock.cost_value > 0) {
        heroes.push({ key: 'stock_value', label: 'Stock Value', formatted: this.formatCurrency(this.stock.cost_value), comparison: null, metadata: {} });
      }
      return heroes.filter(Boolean);
    },

    owedParts() {
      const c = (this.owed && this.owed.customers_owe) || {};
      return [
        { label: 'Unpaid invoices', amount: c.invoices, href: '/reports/receivables' },
        { label: 'Buy Now Pay Later', amount: c.bnpl, href: '/orders?bnpl=1' },
        { label: 'Booking balances', amount: c.bookings, href: '/reservations' },
        { label: 'Membership balances', amount: c.memberships, href: '/subscriptions' },
      ].filter(p => Number(p.amount) > 0);
    },

    hasManufacturing() {
      return !!this.userDetails?.company?.companysettings?.has_manufacturing;
    },

    // Blocks ordered by how much each source brought in this period.
    blockOrder() {
      const series = this.incomeTrend?.series || {};
      const total = k => (series[k] || []).reduce((a, b) => a + b, 0);
      const keys = ['Sales', 'Memberships', 'Reservations'];
      const ranked = [...keys].sort((a, b) => total(b) - total(a));
      // Ties (e.g. no income yet): a gym/hotel's own module first.
      if (this.isHotelOnly && !total('Reservations')) ranked.unshift(...ranked.splice(ranked.indexOf('Reservations'), 1));
      if (this.isGymOnly && !total('Memberships')) ranked.unshift(...ranked.splice(ranked.indexOf('Memberships'), 1));
      return Object.fromEntries(ranked.map((k, i) => [k, i + 1]));
    },

    hasReservations() {
      return !!this.userDetails?.company?.companysettings?.has_reservations;
    },

    hasSubscriptions() {
      return !!this.userDetails?.company?.companysettings?.has_subscriptions;
    },

    hasPosSales() {
      const k = this.kpis.find(x => x.key === 'net_sales');
      return !!(k && Number(k.value) > 0);
    },

    // Till-only panels (staff, time of day, POS payment breakdown): hidden
    // for invoice-only, and for a gym that sold nothing at the till.
    showPosSections() {
      return !this.isInvoiceOnly && (!this.isNoPos || this.hasPosSales);
    },

    // More than one kind of income (or none from the till): show where the
    // money comes from instead of the POS-only sales chart.
    showIncomeChart() {
      const series = this.incomeTrend?.series || {};
      return Object.keys(series).some(k => k !== 'Sales');
    },

    isGymOnly() {
      const s = this.userDetails?.company?.companysettings;
      return s?.primary_use_case === 'subscriptions' && !!s?.has_subscriptions;
    },

    isHotelOnly() {
      const s = this.userDetails?.company?.companysettings;
      return s?.primary_use_case === 'reservations' && !!s?.has_reservations;
    },

    // Gym or hotel mode: no till, so till-only panels hide when empty.
    isNoPos() {
      return this.isGymOnly || this.isHotelOnly;
    },

    secondaryKpis() {
      const heroKeys = this.heroKpis.map(k => k.key);
      return this.kpis.filter(k => !heroKeys.includes(k.key)
        && !(this.isNoPos && POS_ONLY_KEYS.includes(k.key) && !Number(k.value)));
    },

    heroHelp() {
      return {
        total_income: 'Everything charged in this period - sales, invoices, memberships and reservations - excluding VAT. Profit is worked out from this.',
        cash_received: 'All money that came in during this period (M-Pesa, cash, card...), including member wallet top-ups.',
        net_profit: 'Total Income minus cost of goods sold, expenses, wastage and balances waived.',
        stock_value: 'What your stock on hand cost you (units × buying price), right now. Same as the Inventory page.',
      };
    },
  },

  watch: {
    dayOfWeek() {
      this.fetchTimeOfDay();
    },
    customRange: {
      deep: true,
      handler(range) {
        if (!range.startDate || !range.endDate) return;

        const iso = d => new Date(d).toISOString().split('T')[0];
        const startDate = iso(range.startDate);
        const endDate   = iso(range.endDate);

        // Guard against refetching on every intermediate click while a user
        // is still adjusting the picker (deep watcher fires whenever both
        // dates happen to be set, not just once the selection is final) -
        // only hit the heavy executive-summary endpoint when the resolved
        // range actually changed.
        if (startDate === this.filters.startDate && endDate === this.filters.endDate) return;

        this.filters.startDate = startDate;
        this.filters.endDate   = endDate;
        this.fetchDashboardData();
      }
    }
  },

  created() {
    // Arriving with dates in the URL (e.g. back from a category drill-down).
    const params = new URLSearchParams(window.location.search);
    const start = params.get('start-date');
    const end = params.get('end-date');
    if (start && end) {
      const iso = d => moment(d).format('YYYY-MM-DD');
      const today = moment().format('YYYY-MM-DD');
      if (iso(start) === today && iso(end) === today) return;
      this.dateRange = 'custom';
      this.filters.startDate = iso(start);
      this.filters.endDate = iso(end);
      this.customRange = { startDate: new Date(this.filters.startDate), endDate: new Date(this.filters.endDate) };
    }
  },

  mounted() {
    recordVisit('beta');
    this.loadBranches();
    this.fetchDashboardData();
    this.fetchDukakitBalance();
    this.fetchFinanceHighlights();
    this.startAutoRefresh();
  },

  beforeUnmount() {
    this.stopAutoRefresh();
  },

  methods: {
    // Each "branch" is a separate Company row (see CompanyBranches); there's
    // no branch_id column to filter by within a query, so viewing another
    // branch's numbers means switching the active session to it — same
    // mechanism as the branch switcher in the sidebar.
    loadBranches() {
      const own = this.userDetails?.companybranches || [];
      this.branches = own
        .filter(b => b.company && b.company.id !== this.currentCompanyId)
        .map(b => ({ id: b.company.id, name: b.company.name }));
      this.filters.branchId = this.currentCompanyId;
    },
    async onBranchChange() {
      if (this.filters.branchId === this.currentCompanyId) return;
      try {
        await branchSwitch.switchTo(this.$store, this.filters.branchId);
      } catch (e) {
        this.filters.branchId = this.currentCompanyId;
      }
    },
    async fetchFinanceHighlights() {
      try {
        const { data } = await axios.get('/api/reports/finance/highlights');
        this.financeHighlights = data;
      } catch (e) {
        // Non-critical sidebar panel; the rest of the dashboard still works without it.
      }
    },
    async fetchDashboardData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get('/api/sales-analytics/executive-summary', {
          params: {
            start_date:          this.filters.startDate,
            end_date:            this.filters.endDate,
            branch_id:           this.filters.branchId,
            compare_to_previous: 1
          }
        });

        this.kpis            = this.transformKpis(response.data.kpis);
        this.chartData       = this.transformChartData(response.data);
        this.paymentBreakdown = response.data.payment_breakdown;
        this.bnpl            = response.data.bnpl || this.bnpl;
        this.topProducts     = response.data.top_products;
        this.categoryPerformance = response.data.category_performance || [];

        await Promise.allSettled([
          this.fetchIncomeTrend(),
          this.fetchStock(),
          this.fetchOwed(),
          this.fetchTimeOfDay(),
          this.fetchStaffPerformance(),
          this.fetchAlerts(),
          this.fetchShiftClosingMetrics()
        ]);

      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to load dashboard data';
        console.error('Dashboard error:', err);
      } finally {
        this.loading = false;
      }
    },

    async fetchStock() {
      try {
        const { data } = await axios.get('/api/sales-analytics/stock');
        this.stock = data;
      } catch (e) {
        this.stock = null; // no inventory permission
      }
    },

    async fetchOwed() {
      try {
        const { data } = await axios.get('/api/sales-analytics/owed');
        this.owed = data;
      } catch (e) {
        this.owed = null; // no financial-reports permission
      }
    },

    async fetchIncomeTrend() {
      try {
        const { data } = await axios.get('/api/sales-analytics/income-trend', {
          params: { start_date: this.filters.startDate, end_date: this.filters.endDate }
        });
        this.incomeTrend = data;
      } catch (err) {
        this.incomeTrend = null;
      }
    },

    async fetchTimeOfDay() {
      try {
        const { data } = await axios.get('/api/analytics-sales', {
          params: { start_date: this.filters.startDate, end_date: this.filters.endDate, day_of_week: this.dayOfWeek || undefined }
        });
        this.timeOfDayChart = {
          labels: Object.keys(data || {}),
          datasets: [{ label: 'Sales', backgroundColor: '#fd4719', borderColor: '#fd4719', data: Object.values(data || {}) }]
        };
      } catch (err) {
        this.timeOfDayChart = { labels: [], datasets: [] };
      }
    },

    async fetchStaffPerformance() {
      try {
        const response = await axios.get('/api/sales-analytics/metrics/staff', {
          params: {
            start_date: this.filters.startDate,
            end_date:   this.filters.endDate,
            branch_id:  this.filters.branchId
          }
        });
        this.staffPerformance = response.data;
      } catch (err) {
        console.error('Staff performance error:', err);
      }
    },

    async fetchAlerts() {
      try {
        const response = await axios.get('/api/sales-analytics/alerts-trends', {
          params: {
            start_date: this.filters.startDate,
            end_date:   this.filters.endDate
          }
        });
        this.alerts = response.data.alerts;
      } catch (err) {
        console.error('Alerts error:', err);
      }
    },

    async downloadAccountReport(type) {
      const endpoints = {
        pnl: '/api/sales-analytics/reports/retail/profit-loss/download',
        salestax: '/api/sales-analytics/reports/retail/sales-tax/download',
        generalledger: '/api/sales-analytics/reports/retail/general-ledger/download',
      };

      const url = endpoints[type];
      if (!url) return;

      this.downloadingReport = type;

      try {
        const res = await axios.post(url, {
          start_date: this.filters.startDate,
          end_date: this.filters.endDate,
          format: 'pdf',
        });

        if (res.data && res.data.success) {
          this.triggerDownload(res.data.data);
        }
      } catch (err) {
        console.error('Report download failed:', err);
      } finally {
        this.downloadingReport = null;
      }
    },

    triggerDownload(file) {
      return downloadFile(file.url || file.download_url || file.report_url, file.filename || 'report.pdf');
    },

    handleDateRangeChange() {
      const today = new Date();
      const iso = d => d.toISOString().split('T')[0];

      switch (this.dateRange) {
        case 'today':
          this.filters.startDate = iso(today);
          this.filters.endDate   = iso(today);
          this.trendViewMode     = 'hourly';
          break;
        case 'yesterday': {
          const y = new Date(today);
          y.setDate(y.getDate() - 1);
          this.filters.startDate = iso(y);
          this.filters.endDate   = iso(y);
          this.trendViewMode     = 'hourly';
          break;
        }
        case 'week': {
          const w = new Date(today);
          w.setDate(today.getDate() - 7);
          this.filters.startDate = iso(w);
          this.filters.endDate   = iso(today);
          this.trendViewMode     = 'daily';
          break;
        }
        case 'month': {
          const m = new Date(today.getFullYear(), today.getMonth(), 1);
          this.filters.startDate = iso(m);
          this.filters.endDate   = iso(today);
          this.trendViewMode     = 'daily';
          break;
        }
        case 'custom':
          this.customRange = {
            startDate: new Date(this.filters.startDate),
            endDate: new Date(this.filters.endDate)
          };
          return;
      }

      this.fetchDashboardData();
    },

    formatPickerDate(date) {
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    transformChartData(apiData) {
      const trend = apiData.charts?.sales_trend || [];

      const build = (labelFn) => ({
        labels:  trend.map(i => labelFn(i.date || i.period)),
        sales:   trend.map(i => i.gross_sales    || 0),
        net:     trend.map(i => i.net_sales       || 0),
        orders:  trend.map(i => i.total_orders    || 0),
        profit:  trend.map(i => i.gross_profit    || 0),
        aov:     trend.map(i => i.avg_order_value || 0),
        invoices: trend.map(i => i.invoice_sales  || 0)
      });

      const fmt = (dateStr, mode) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (mode === 'hour')  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        if (mode === 'month') return d.toLocaleDateString('en-US', { month: 'short' });
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      };

      return {
        day:   build(d => fmt(d, 'hour')),
        week:  build(d => fmt(d, 'day')),
        month: build(d => fmt(d, 'month'))
      };
    },

    trendClass(kpi) {
      if (!kpi || !kpi.comparison) return 'text-secondary';
      const positiveMetrics = ['total_income', 'cash_received', 'total_sales', 'invoice_sales', 'gross_sales', 'net_sales', 'total_orders', 'gross_profit', 'net_profit'];
      const negativeMetrics = ['discount_total', 'refund_total', 'void_total', 'total_expenses', 'deductible_expenses', 'non_deductible_expenses', 'total_wastes', 'bad_debts'];
      const dir = kpi.comparison.direction;
      let sentiment = 'neutral';
      if (positiveMetrics.includes(kpi.key)) sentiment = dir === 'up' ? 'positive' : 'negative';
      else if (negativeMetrics.includes(kpi.key)) sentiment = dir === 'up' ? 'negative' : 'positive';
      return sentiment === 'positive' ? 'text-success' : sentiment === 'negative' ? 'text-danger' : 'text-secondary';
    },

    // Small "where it came from" line under a hero number; only shown when
    // more than one source contributed.
    heroParts(kpi) {
      const m = kpi.metadata || {};
      const parts = kpi.key === 'total_income'
        ? [['Sales', m.sales], ['Invoices', m.invoices], ['Memberships', m.memberships], ['Reservations', m.reservations]]
        : kpi.key === 'cash_received' ? Object.entries(m.by_source || {}) : [];
      const nonZero = parts.filter(([, a]) => Number(a) > 0).map(([label, amount]) => ({ label, amount }));
      return nonZero.length > 1 ? nonZero : [];
    },

    transformKpis(kpisData) {
      return Object.entries(kpisData).map(([key, data]) => ({
        key,
        label:      this.formatKpiLabel(key),
        value:      data.value,
        formatted:  data.formatted,
        comparison: data.comparison,
        metadata:   data
      }));
    },

    formatKpiLabel(key) {
      const labels = {
        total_expenses:        'Total Expenses',
        deductible_expenses:   'Expenses (from Sales)',
        non_deductible_expenses: 'Expenses (External / Monthly)',
        gross_sales:           'Gross Sales',
        total_income:          'Total Income (excl. VAT)',
        cash_received:         'Cash Received',
        total_sales:           'Total Sales (POS + Invoices)',
        net_sales:             'Net Sales (POS)',
        gross_profit:          'Gross Profit',
        net_profit:            'Net Profit',
        total_orders:          'Total Orders',
        pending_orders:        'Pending Orders',
        avg_order_value:       'Avg Order Value',
        total_items_sold:      'Items Sold',
        avg_items_per_order:   'Avg Items / Order',
        discount_total:        'Discounts',
        tax_collected:         'Tax Collected',
        refund_total:          'Refunds',
        void_total:            'Voids',
        outstanding_balance:   'Outstanding Balance',
        total_wastes:          'Wastes',
        other_income:          'Other Income',
        bad_debts:             'Waived (bad debts)',
        invoice_sales:         'Invoice Sales',
      };
      return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    async fetchShiftClosingMetrics() {
      try {
        const { data } = await axios.get('/api/shift-cash-closings/metrics');
        this.shiftClosingMetrics = data;
      } catch (_) {
        // Non-critical — silently skip if shifts not enabled or no permission
      }
    },

    async fetchDukakitBalance() {
      try {
        const { data } = await axios.get('/api/dukakit-account/balance');
        this.dukakitAccountBalance = data || 0;
        this.hasDukakitPayAccount = true;
      } catch (err) {
        if (err.response && err.response.status === 433) {
          this.hasDukakitPayAccount = false;
        }
      }
    },

    openWithdrawModal() {
      this.showWithdrawModal = true;
    },

    formatCurrency(value) {
      return 'KES ' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // Classic "Download Full Report": built in the background and emailed.
    async emailFullReport() {
      let fresh = false;
      try {
        const reuse = await this.$bvModal.msgBoxConfirm('To save time, the report for a period is kept the first time it is generated. Send that copy, or generate a new one?', {
          title: 'Email full report',
          okVariant: 'primary',
          okTitle: 'Send report',
          cancelTitle: 'Generate new report',
          centered: true
        });
        if (reuse === null) return;
        fresh = reuse === false;
        await axios.get('/api/reports-daily', {
          params: {
            start_date: this.filters.startDate,
            end_date: this.filters.endDate,
            day_of_week: this.dayOfWeek || undefined,
            from_dashboard: true,
            new_report: fresh ? 1 : 0
          }
        });
        this.$bvModal.msgBoxOk(`We will email the report to ${this.userDetails.email}.`, { title: 'Report requested', centered: true });
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Could not request the report.' });
      }
    },

    handleReturnToClassic() {
      setPreferredDashboard('classic');
    },

    dismissAlert(alertType) {
      this.alerts = this.alerts.filter(a => a.type !== alertType);
    },

    scrollToAlerts() {
      this.$refs.alertsAnchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    startAutoRefresh() {
      this.stopAutoRefresh();
      if (this.dateRange === 'today') {
        this.autoRefreshInterval = setInterval(this.fetchDashboardData, 300000);
      }
    },

    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    }
  }
};
</script>

<style scoped>
.custom-range-picker ::v-deep .reportrange-text {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  height: auto !important;
}

.skeleton {
  background: #f1f5f9;
  animation: shimmer 1.4s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.spin {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

.quick-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  border-radius: 0;
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  text-decoration: none;
  background: transparent;
  text-align: left;
}

.quick-action-row:last-child {
  border-bottom: none;
}

.quick-action-row:hover {
  color: #0f172a;
  text-decoration: none;
}

.btn.quick-action-row:focus {
  box-shadow: none;
}
</style>
